import * as React from 'react';
import { translate } from '@avensia/excite';
import { styled } from '@glitz/react';
import { Button } from 'webapp/components/button';
import { createThemeDecorator, Theme } from 'webapp/theme';
import { FieldSize } from 'webapp/components/field-size';
import { createBorderDecorator } from 'webapp/components/form/text-field';
import { SlideHeightAnimation } from '@avensia/excite-ui';
import { darkGrey } from 'webapp/shared/sharedTheme';

type PropType = {
  feedbackUrl: string;
  showReportForm: boolean;
  setShowReportForm: (show: boolean) => void;
};

export const ReportReview = ({ feedbackUrl, showReportForm, setShowReportForm }: PropType) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [textareaValue, setTextareaValue] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState<boolean>(false);
  const [showFeedbackMessage, setShowFeedbackMessage] = React.useState<boolean>(false);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaValue(e.target.value);
  };

  const reportReview = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await fetch(`${feedbackUrl}?type=bad&comment=${textareaValue}`);
      setShowFeedbackMessage(true);
    } catch (e) {
      console.warn("couldn't send feedback", e);
      setErrorMessage(true);
    }
  };

  return (
    <HeightAnimation keepElement visible={showReportForm}>
      {showFeedbackMessage ? (
        <SubmitFeedback>{translate(t => t.productPage.reviewsReportSubmitFeedback)}</SubmitFeedback>
      ) : (
        <ReportForm onSubmit={reportReview}>
          <Container>
            <Textarea
              ref={textareaRef}
              placeholder={translate(t => t.productPage.reviewsReportPlaceholder)}
              value={textareaValue}
              onChange={handleTextareaChange}
            />
            {errorMessage && <ErrorMessage>{translate(t => t.productPage.reviewsReportSubmitError)}</ErrorMessage>}
          </Container>
          <Button size={FieldSize.XSmall} onClick={() => setShowReportForm(false)} type="button" as={Theme.Monochrome}>
            {translate(t => t.productPage.reviewsReportCancel)}
          </Button>
          <Button size={FieldSize.XSmall} type="submit" disabled={textareaValue === ''}>
            {translate(t => t.productPage.reviewsReportSubmit)}
          </Button>
        </ReportForm>
      )}
    </HeightAnimation>
  );
};

const HeightAnimation = styled(SlideHeightAnimation, {
  gridColumnStart: 'span 2',
  maxWidth: 400,
  width: '100%',
  justifySelf: 'flex-end',
});

const ReportForm = styled.form({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: t => t.sharedSpacing.m,
  width: '100%',
});

const Container = styled.div({
  gridColumnStart: 'span 2',
});

const SubmitFeedback = styled(Container, {
  textAlign: 'right',
  fontSize: t => t.sharedFontSize.xxs,
  color: darkGrey,
});

/** @glitz-dynamic */
const Textarea = styled.textarea(createThemeDecorator(Theme.White), createBorderDecorator(Theme.White), {
  width: '100%',
  padding: { xy: t => t.sharedSpacing.xs },
  '::placeholder': {
    color: t => t.monochrome.text,
    opacity: 0.5,
  },
  ':focus': {
    outline: { color: t => t.inputFocusOutlineColor, width: 1, style: 'solid' },
    border: { xy: { color: t => t.inputFocusOutlineColor } },
  },
});

const ErrorMessage = styled.p({
  color: t => t.negative.color,
  fontSize: t => t.sharedFontSize.xxs,
  marginBottom: 0,
});
